import {
	Fragment,
	ReactElement,
	ReadonlyRefObject,
	Ref,
	useCallback,
	useImperativeHandle,
	useMemo,
	useRef,
	useState
} from "react";
import classNames from "classnames";
import { CalendarCell } from "./CalendarCell";
import styles from "./MonthView.module.less";
import { Week } from "./Week";
import { WeekDays } from "./WeekDays";
import { DateOnly } from "client/DateAndTime/dateOnly";
import {
	addDays,
	daysInAWeek,
	generateDateArray,
	getDayOfWeek,
	getWeekNumber,
	isBetween
} from "client/DateAndTime/dateUtils";
import { DayOfWeek } from "client/DateAndTime/culture";
import {
	CalendarHighlightDateOnlyRange,
	CalendarNavigationKey,
	isCalendarNavigationKey
} from "./Calendar";
import { isNil } from "client/Misc/utils";
import { KeyEventArguments } from "../TextInput/TextInput";
import { Key } from "client/Misc/commonTypes";
import { NormalizedMap } from "client_legacy/ndaw/normalizedMap";

export type MonthViewHandle = {
	focusDate(date: DateOnly): void;
};

export type MonthViewProps = {
	firstWeekDay: DayOfWeek;
	year: number;
	month: number;
	today: DateOnly;
	highlightRange: CalendarHighlightDateOnlyRange | null;
	onSelect(value: DateOnly): void;
	showWeekNumbers: boolean | undefined;
	eventDateMap: Map<DateOnly, boolean>;
	calculateHighlightRangeForDate(date: DateOnly): CalendarHighlightDateOnlyRange;
	onViewRangeChange: (modifier: 1 | -1) => void;
	minDate: DateOnly | null;
	maxDate: DateOnly | null;
	ref: Ref<MonthViewHandle>;
};

export const MonthView = (props: MonthViewProps): ReactElement => {
	const {
		firstWeekDay,
		year,
		month,
		today,
		highlightRange,
		onSelect,
		showWeekNumbers = false,
		eventDateMap,
		calculateHighlightRangeForDate,
		onViewRangeChange,
		minDate,
		maxDate,
		ref
	} = props;

	const [hoveredDate, setHoveredDate] = useState<DateOnly | null>(null);
	const [focusableDate, setFocusableDate] = useState<DateOnly | undefined>(undefined);

	const itemRefs: ReadonlyRefObject<
		NormalizedMap<DateOnly, HTMLElement | null>
	> = useRef<NormalizedMap<DateOnly, HTMLElement | null>>(
		new NormalizedMap<DateOnly, HTMLElement | null>(x => x.toISOString())
	);

	const ranges: WeekRange[] = generateWeekRangesForMonth(year, month, firstWeekDay);

	const isDateInView = useCallback(
		(date: DateOnly): boolean => {
			const lastWeekRangeDates: readonly DateOnly[] = ranges[ranges.length - 1].dates;

			return isBetween(
				date,
				ranges[0].dates[0],
				addDays(
					lastWeekRangeDates[lastWeekRangeDates.length - 1],
					1
				)
			);
		},
		[ranges]
	);

	const computedFocusableDate = useMemo(
		(): DateOnly => {
			if (isNil(focusableDate) || !isDateInView(focusableDate)) {
				return new DateOnly(
					year,
					month,
					1
				);
			}

			return focusableDate;
		},
		[
			focusableDate,
			isDateInView,
			month,
			year
		]
	);

	const hoverRange = useMemo(
		(): CalendarHighlightDateOnlyRange | null => {
			if (!hoveredDate) {
				return null;
			}

			return calculateHighlightRangeForDate(hoveredDate);
		},
		[calculateHighlightRangeForDate, hoveredDate]
	);

	const setItemRef = useCallback(
		(item: HTMLElement | null, date: DateOnly): void => {
			if (isNil(item)) {
				return;
			}

			itemRefs.current
				.set(date, item);
		},
		[]
	);

	const focusItemHandler = useCallback(
		(targetDate: DateOnly): void => {
			setFocusableDate(targetDate);

			// The setTimeout is used to delay focusing the target month until after the view range has changed
			setTimeout(
				(): void => {
					itemRefs.current
						?.get(targetDate)
						?.focus();
				},
				1
			);
		},
		[]
	);

	const movementHandler = (args: KeyEventArguments, selectedDate: DateOnly): boolean => {
		if (isNil(args.key) || !isCalendarNavigationKey(args.key)) {
			return false;
		}

		const keyToOffset: Record<CalendarNavigationKey, number> = {
			[Key.ArrowDown]: daysInAWeek,
			[Key.ArrowUp]: -daysInAWeek,
			[Key.ArrowLeft]: -1,
			[Key.ArrowRight]: 1
		};

		const offset: number = keyToOffset[args.key];
		const targetDate: DateOnly = addDays(selectedDate, offset);

		if (!isDateInView(targetDate)) {
			onViewRangeChange(offset > 0 ? 1 : -1);
		}

		focusItemHandler(targetDate);

		return true;
	};

	useImperativeHandle(
		ref,
		(): MonthViewHandle => ({
			focusDate: (date: DateOnly): void => {
				focusItemHandler(date);
			}
		}),
		[focusItemHandler]
	);

	return (
		<div
			className={classNames(
				styles.root,
				{
					[styles.root_withWeekNumbers]: showWeekNumbers,
				}
			)}
			role="grid"
		>
			{showWeekNumbers && (
				<CalendarCell
					trackingId="month-header"
					kind="header"
				/>
			)}

			<WeekDays firstWeekDay={firstWeekDay} />

			{ranges.map((x) => (
				<Fragment key={x.weekNumber}>
					{showWeekNumbers && (
						<CalendarCell
							trackingId={`week-number|${x.weekNumber}`}
							kind="week-number"
						>
							{ x.weekNumber }
						</CalendarCell>
					)}
					<Week
						dates={x.dates}
						highlightRange={highlightRange}
						hoverRange={hoverRange}
						onHover={setHoveredDate}
						onSelect={onSelect}
						today={today}
						eventDateMap={eventDateMap}
						onOutOfRangeEventCheck={
							(date: DateOnly): boolean =>
								month !== date.monthIndex || year !== date.year
						}
						minDate={minDate}
						maxDate={maxDate}
						focusableDate={computedFocusableDate}
						setItemRef={setItemRef}
						onMovement={movementHandler}
					/>
				</Fragment>
			))}
		</div>
	);
};
MonthView.displayName = "MonthView";

type WeekRange = {
	dates: readonly DateOnly[];
	weekNumber: number;
};

function generateWeekRangesForMonth(
	year: number,
	month: number,
	firstWeekDay: number
): WeekRange[] {
	const result: WeekRange[] = [];
	const firstDayInMonthWeekDay: DayOfWeek = getDayOfWeek(new DateOnly(year, month, 1));

	let amountOfPrefix: number = 0;
	if (firstDayInMonthWeekDay !== firstWeekDay) {
		amountOfPrefix = firstDayInMonthWeekDay - firstWeekDay;
		if (amountOfPrefix < 0) {
			amountOfPrefix = 7 + amountOfPrefix;
		}
	}

	let weekStart: DateOnly = addDays(
		new DateOnly(year, month, 1),
		-amountOfPrefix
	);

	for (let i = 0; i < 6; i++) {
		const dates: readonly DateOnly[] = generateDateArray(weekStart, 7);

		result.push({
			dates,
			weekNumber: getWeekNumber(weekStart)
		});

		weekStart = addDays(weekStart, 7);
	}

	return result;
}